import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, Plus, ChevronRight,
  TrendingUp, AlertCircle, CalendarDays, Shield, Sparkles,
  Thermometer, Sun, Cake,
  User as UserIcon,
  CircleCheck, MapPin, Network, Palmtree, Home, Heart, 
  Briefcase, Baby, GraduationCap, Stethoscope, Coffee, Umbrella,
  ChevronDown, ChevronUp, Activity, Inbox
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import DashboardOverview from '../components/DashboardOverview';
import ToilClaimsQueue from '../components/toil/ToilClaimsQueue';
import YourPendingRequests from '../components/YourPendingRequests';

// Render any ISO date (YYYY-MM-DD) inside free text as UK DD/MM/YYYY
const ukDates = (str) => (str || '').replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, m, d) => `${d}/${m}/${y}`);
import RequestLeaveDialog from '../components/RequestLeaveDialog';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, leaveApi, onboardingApi, holidaysApi, toilApi } from '../lib/api';
import { formatLeave } from '../lib/leaveUnits';
import { formatDateGB } from '../lib/dates';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { format, parseISO } from 'date-fns';
import { bookingLength, withTimes } from '../lib/bookingTime';
import CancelLeaveDialog from '../components/CancelLeaveDialog';

// The Team Wallchart that used to sit at the bottom of this page is now its own page,
// pages/TeamCalendarPage.js (Warm Hive decision 9, 29/10/2026), with "Block Leave"
// (now "No-leave dates") in its header.

/**
 * One request in an approver's queue.
 *
 * ⚠️ MODULE LEVEL ON PURPOSE. A component declared inside another component is a brand
 * new type on every render, so React unmounts and remounts the whole list each time.
 * That is exactly what froze the browser on the org chart (BUGS.md F42) — don't move
 * this inside DashboardPage.
 *
 * What's visible when collapsed is anything that could CHANGE the decision: the two
 * balance outcomes, whether the requester's own team is short, and any policy
 * exception. What sits behind "More detail" is what you'd only read once the answer is
 * "hmm" — who exactly is off and when, and their other absence this leave year. A
 * queue of eight has to stay skimmable or nobody reads any of it.
 *
 * `req.context` can be null: the backend builds it best-effort, because a context
 * failure must never empty somebody's approvals queue. Everything below degrades to
 * the request on its own, which is what this card showed before.
 */
const PendingApprovalCard = ({ req, viewerId, expanded, onToggle, highlighted, onApprove, onDecline, cardRef }) => {
  const ctx = req.context || null;
  const unit = (ctx && ctx.display_unit) || req.display_unit || 'days';
  const bal = (ctx && ctx.balance) || {};
  const peers = (ctx && ctx.peers) || null;
  const otherAbsence = (ctx && ctx.other_absence) || [];

  const fmt = (days, minutes) => formatLeave(days, { unit, minutes });

  // "2 of 6 in Sales are off then". The size counts the whole department INCLUDING the
  // requester, which is how a human reads it: six in the team, two of the others away.
  let teamLine = null;
  if (peers) {
    const offCount = (peers.in_department || []).length;
    if (peers.department_name) {
      teamLine = offCount === 0
        ? `Nobody else in ${peers.department_name} is off then`
        : `${offCount} of ${peers.department_size} in ${peers.department_name} are off then`;
    } else {
      // No department set, so we can't honestly say "2 of 6 in Sales". Fall back to the
      // company picture and say that's what it is, rather than implying a team.
      teamLine = peers.company_wide_count === 0
        ? 'Nobody else is off then'
        : `${peers.company_wide_count} off company-wide then`;
    }
  }
  const teamIsClear = peers && peers.department_name
    ? (peers.in_department || []).length === 0
    : peers && peers.company_wide_count === 0;

  return (
    <div
      ref={cardRef}
      className={`p-4 bg-secondary/50 rounded-lg ${highlighted ? 'ring-2 ring-primary' : ''}`}
      data-testid={`approval-card-${req.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-3 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: req.leave_type_color }} />
          <div className="min-w-0">
            <p className="font-medium">{req.user_name}</p>
            {/*
              ⚠️ THE DATES AND THE PART OF THE DAY ARE ONE PHRASE, from the backend —
              "afternoon of 26/10/2026 - morning of 27/10/2026". Rob's wording,
              01/09/2026: a bracketed note on the end ("... from afternoon, until
              morning") made the reader hold two facts apart and join them up.
              Attaching each part to its own date says it once.

              Built server-side so this, the approver's email, the Teams card and
              the .ics event cannot describe the same booking differently — and
              the fallback keeps an old cached response rendering rather than
              showing a blank line.
            */}
            <p className="text-sm text-muted-foreground">
              {req.leave_type} • {req.dates_phrase
                || withTimes(`${formatDateGB(req.start_date)}${req.start_date !== req.end_date ? ` - ${formatDateGB(req.end_date)}` : ''}`, req)} ({bookingLength(req, unit)})
            </p>
            {req.reason && (
              <p className="text-sm text-foreground/80 mt-1 italic">&ldquo;{req.reason}&rdquo;</p>
            )}
          </div>
        </div>
        {req.total_approval_levels > 1 && (
          <span className="text-xs text-muted-foreground whitespace-nowrap bg-background/60 px-2 py-1 rounded">
            {req.approval_level_name || `Level ${req.current_approval_level}`} ({req.current_approval_level}/{req.total_approval_levels})
            {req.is_backup && <span className="ml-1 text-blue-500">(Backup)</span>}
          </span>
        )}
      </div>

      {ctx && (
        <>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {/* Approving does NOT move `remaining` — the request is already deducted as
                pending. The honest pair is "where it stays" vs "what goes back". See
                services/approval_context.balance_outcomes for the full explanation. */}
            <div className="rounded-lg bg-background/70 px-3 py-2">
              <p className="text-xs text-muted-foreground">Left if you approve</p>
              <p className="text-lg font-semibold">{fmt(bal.if_approved_days, bal.if_approved_minutes)}</p>
            </div>
            <div className="rounded-lg bg-background/70 px-3 py-2">
              <p className="text-xs text-muted-foreground">Left if you decline</p>
              <p className="text-lg font-semibold">{fmt(bal.if_declined_days, bal.if_declined_minutes)}</p>
            </div>
            <div className="rounded-lg bg-background/70 px-3 py-2">
              <p className="text-xs text-muted-foreground">Booked later</p>
              <p className="text-lg font-semibold">{fmt(ctx.booked_later_days, ctx.booked_later_minutes)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Leave year {formatDateGB(ctx.leave_year_start)} – {formatDateGB(ctx.leave_year_end)}
          </p>

          {teamLine && (
            <p className={`text-xs mt-2.5 flex items-center gap-1.5 ${teamIsClear ? 'text-emerald-600' : 'text-foreground'}`}>
              <Users className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                {teamLine}
                {peers.pending_count > 0 && (
                  <span className="text-muted-foreground">
                    {' '}· {peers.pending_count} more awaiting a decision
                  </span>
                )}
              </span>
            </p>
          )}
        </>
      )}

      {req.is_escalated && (
        <p className="text-xs text-amber-600 font-medium mt-2 flex items-center gap-1">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          {/* An admin can be the overdue approver themselves — in a multi-tier policy
              where a later tier IS an admin. Naming them back to themselves reads as a
              bug, so say what it means instead. */}
          {req.escalated_from && req.escalated_from_id !== viewerId
            ? `Passed to you — ${req.escalated_from} didn't respond`
            : 'Overdue — now visible to all admins'}
        </p>
      )}

      {req.limit_exception && (
        <p className="text-xs text-red-600 font-bold mt-2 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>Exception — {ukDates(req.limit_exception_note) || 'exceeds a leave limit'}</span>
        </p>
      )}

      {ctx && expanded && (
        <div className="mt-3 pt-3 border-t border-border space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Who else is off</p>
            {(peers.in_department || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nobody in their team.</p>
            ) : (
              <div className="space-y-1">
                {peers.in_department.map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span>
                      {p.name}
                      {p.department && <span className="text-muted-foreground"> · {p.department}</span>}
                    </span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {formatDateGB(p.start_date)} – {formatDateGB(p.end_date)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1.5">
              {peers.company_wide_count} {peers.company_wide_count === 1 ? 'person' : 'people'} off company-wide over those dates
            </p>
          </div>

          {otherAbsence.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5" /> Other absence this leave year
              </p>
              <div className="space-y-1">
                {otherAbsence.map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span>{a.leave_type_name}</span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {formatLeave(a.taken_days, { unit, minutes: a.taken_minutes })} · {a.occasions} {a.occasions === 1 ? 'occasion' : 'occasions'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-3">
        <div className="flex gap-2">
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-500 text-white"
            onClick={() => onApprove(req.id)}
            data-testid={`approve-${req.id}`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onDecline(req.id)}
            data-testid={`decline-${req.id}`}
          >
            Decline
          </Button>
        </div>
        {ctx && (
          <button
            type="button"
            onClick={() => onToggle(req.id)}
            className="text-sm text-primary hover:underline flex items-center gap-1 whitespace-nowrap"
            data-testid={`detail-toggle-${req.id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Less detail' : 'More detail'}
          </button>
        )}
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // /approvals renders this same component (see App.js). Rather than two near-identical
  // pages, this flag switches between "the dashboard" and "just the approvals queue".
  const isApprovalsPage = location.pathname === '/approvals';
  // The approver email links to ONE request (/approvals?request=<id>) rather than to
  // the top of the queue. With eight items waiting, "Review request" used to drop you
  // in a list and leave you to find it.
  const [searchParams] = useSearchParams();
  const linkedRequestId = searchParams.get('request');
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0); // For refreshing sidebar badge
  const [stats, setStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const firstLoadRef = useRef(true);  // only the first load shows the full collapse spinner
  const [loading, setLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  
  // Request Leave dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [quickBookDate, setQuickBookDate] = useState(null);
  const [quickBookLeaveTypeId, setQuickBookLeaveTypeId] = useState(null);
  
  const [bookForUser, setBookForUser] = useState(null);
  // Time in lieu claims waiting on this person (DESIGN-toil.md screen 4) — so Approvals only
  // says "Nothing waiting" when there are neither claims nor leave requests.
  const [toilWaiting, setToilWaiting] = useState(0);
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);  // bump to refresh DashboardOverview widgets
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineNote, setDeclineNote] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  // Which approval cards have their detail drawer open. Keyed by request id so the
  // list can re-fetch under us without a card silently closing or opening.
  const [expandedApprovals, setExpandedApprovals] = useState({});
  const linkedCardRef = useRef(null);
  const linkedHandledRef = useRef(null);

  // The dashboard's approvals strip counts time-in-lieu claims as well as leave, like the
  // sidebar badge (Warm Hive, 28/10/2026). On /approvals the queue itself reports this.
  useEffect(() => {
    if (isApprovalsPage) return;
    if (!(user?.role === 'manager' || user?.role === 'admin' || user?.role === 'superadmin')) return;
    toilApi.pending().then((r) => setToilWaiting((r.data?.claims || []).length)).catch(() => {});
  }, [isApprovalsPage, user, sidebarRefreshTrigger]);

  const toggleApprovalDetail = (id) => {
    setExpandedApprovals((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Arriving from the approver email: open that request's detail and scroll to it.
  // Guarded by a ref so the periodic re-fetch of the queue doesn't yank the page back
  // to the card every time — the same deep-link-guard pattern used elsewhere in here.
  useEffect(() => {
    if (!linkedRequestId) return;
    if (linkedHandledRef.current === linkedRequestId) return;
    if (!pendingApprovals.some((r) => r.id === linkedRequestId)) return;
    linkedHandledRef.current = linkedRequestId;
    setExpandedApprovals((prev) => ({ ...prev, [linkedRequestId]: true }));
    // The ref only attaches once the card has rendered as the linked one.
    requestAnimationFrame(() => {
      if (linkedCardRef.current) {
        linkedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [linkedRequestId, pendingApprovals]);

  useEffect(() => {
    // Redirect superadmins to their admin panel
    if (user?.role === 'superadmin') {
      navigate('/superadmin');
      return;
    }
    fetchData();
    fetchOnboardingStatus();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly. `navigate` is stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchOnboardingStatus = async () => {
    try {
      const res = await onboardingApi.getStatus();
      setOnboardingStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    }
  };

  // The cancel box (DESIGN-part-cancel.md): the whole booking or just some of its days.
  // It names whose leave it is itself, so `name` is no longer needed here.
  const [cancelId, setCancelId] = useState(null);
  const handleCancelLeaveFor = (requestId) => {
    if (requestId) setCancelId(requestId);
  };

  const fetchData = async () => {
    if (firstLoadRef.current) setLoading(true);  // later refreshes are silent (no scroll jump)
    try {
      // allSettled, NOT all: with Promise.all, ANY one of these failing blanked
      // the ENTIRE dashboard — wallchart, stats, approvals, the lot. (Found the hard
      // way: one 500 on /leave/allowances emptied the whole Team Wallchart.) Each
      // panel now degrades alone with an empty fallback.
      const settle = (fallback) => (r) => (r.status === 'fulfilled' ? r.value : (
        console.error('Dashboard fetch failed:', r.reason), { data: fallback }
      ));
      const [statsRes, approvalsRes, allowancesRes, holidaysRes, allRequestsRes] = (await Promise.allSettled([
        dashboardApi.getStats(),
        dashboardApi.getPendingApprovals(),
        leaveApi.getMyAllowances(),
        holidaysApi.list(new Date().getFullYear()),
        // Your requests: the pending card and the greeting's "next leave" (not filtered by date)
        leaveApi.listRequests({})
      ])).map((r, i) => settle([null, [], [], [], []][i])(r));

      setStats(statsRes.data);
      setPendingApprovals(approvalsRes.data);
      
      // Process allowances response - now includes bank_holidays info
      const allowancesData = allowancesRes.data;
      setAllowances(allowancesData);
      
      // ⚠️ The company's whole list, across every country it has an office in. The
      // wallchart no longer draws a row from it (step 4) — the only thing derived from
      // it now is the "upcoming bank holidays" panel just below. Anything new that reads
      // it must narrow to one person first; `entry.bank_holiday_dates` is that answer.
      const allHolidays = holidaysRes.data || [];
      
      // Filter for upcoming bank holidays
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = allHolidays
        .filter(h => new Date(h.date) >= today)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setBankHolidays(upcoming);
      
      const allRequests = allRequestsRes.data || [];
      setMyRequests(allRequests);
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      firstLoadRef.current = false;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Refresh the dashboard's data AND the personal overview widgets after any change
  const refreshDashboard = () => {
    fetchData();
    setOverviewRefreshKey(k => k + 1);
  };

  const handleApprove = async (requestId) => {
    try {
      await leaveApi.actionRequest(requestId, 'approve');
      toast.success('Request approved');
      refreshDashboard();
      setSidebarRefreshTrigger(prev => prev + 1); // Refresh sidebar badge
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleDecline = async (requestId, reason) => {
    try {
      await leaveApi.actionRequest(requestId, 'decline', reason || undefined);
      toast.success('Request declined');
      setDeclineTarget(null);
      setDeclineNote('');
      refreshDashboard();
      setSidebarRefreshTrigger(prev => prev + 1); // Refresh sidebar badge
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager' || isAdmin;

  // The greeting's "your next leave starts in N days" — from the requests already loaded,
  // the signed-in person's own approved leave only. Nothing new is fetched for it.
  const nextLeave = (() => {
    const me = String(user?.id || user?.user_id || '');
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const mine = (myRequests || []).filter((r) => String(r.user_id) === me && r.status === 'approved'
      && r.end_date && String(r.end_date).slice(0, 10) >= todayStr);
    if (mine.length === 0) return null;
    const first = mine.map((r) => String(r.start_date).slice(0, 10)).sort()[0];
    if (first <= todayStr) return { days: 0 };
    return { days: Math.round((parseISO(first) - parseISO(todayStr)) / 86400000) };
  })();

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} refreshTrigger={sidebarRefreshTrigger} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        {/* On the dashboard this is the greeting row (Warm Hive, 28/10/2026) and scrolls
            away with the page; on /approvals it stays the sticky title bar it was. */}
        <header className={isApprovalsPage
          ? 'sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border'
          : 'relative z-30'}>
          <div className={isApprovalsPage
            ? 'flex items-center justify-between px-6 py-4'
            : 'flex flex-wrap items-end justify-between gap-x-4 gap-y-3 px-4 sm:px-6 lg:px-8 pt-6 pb-1'}>
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              {isApprovalsPage ? (
                <h1 className="font-heading text-2xl font-bold">Approvals</h1>
              ) : (
                <div className="min-w-0">
                  <h1 className="font-heading text-2xl sm:text-[30px] sm:leading-tight font-semibold">Hi {user?.first_name} 👋</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {format(new Date(), 'EEEE d MMMM yyyy')}
                    {nextLeave && (
                      <>
                        {' · '}
                        {nextLeave.days === 0 ? "You're on leave today" : (
                          <>Your next leave starts {nextLeave.days === 1 ? (
                            <span className="font-bold text-hive-burnt dark:text-hive-orange">tomorrow</span>
                          ) : (
                            <>in <span className="font-bold text-hive-burnt dark:text-hive-orange">{nextLeave.days} days</span></>
                          )}</>
                        )}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                className="btn-primary" 
                data-testid="request-leave-btn"
                onClick={() => setRequestDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Request leave
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:px-8 space-y-5">
          {/* Onboarding Banner (dashboard only — not on the approvals queue) */}
          {!isApprovalsPage && onboardingStatus && !onboardingStatus.onboarding_completed && user?.role === 'admin' && (
            <div className="card-hive p-4 bg-gradient-to-r from-primary/10 to-amber-500/10 border-primary/30" data-testid="onboarding-banner">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold">Complete Your Setup</p>
                    <p className="text-sm text-muted-foreground">
                      {onboardingStatus.completion_percentage}% complete - Add company details to get the most out of HolidayHive
                    </p>
                  </div>
                </div>
                <Button onClick={() => navigate('/onboarding')} className="btn-primary" data-testid="complete-setup-btn">
                  Complete Setup
                </Button>
              </div>
            </div>
          )}
          
          {/* Approvals.
              This component serves BOTH /dashboard and /approvals (see App.js), which
              used to mean the same three-item preview appeared on both — so "View all"
              linked to a page that looked identical and still showed three. Now the
              list lives only on /approvals and shows everything; the dashboard keeps
              the sidebar badge as the prompt. */}
          {isApprovalsPage && (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'superadmin') && (
            <ToilClaimsQueue onCount={setToilWaiting} />
          )}
          {isApprovalsPage && pendingApprovals.length === 0 && toilWaiting === 0 && (
            <div className="card-hive p-6 text-center text-muted-foreground">
              <CircleCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="font-medium text-foreground">Nothing waiting for you</p>
              <p className="text-sm mt-1">Leave requests needing your decision will appear here.</p>
            </div>
          )}
          {isApprovalsPage && pendingApprovals.length > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
            <div className="card-hive p-6" data-testid="pending-approvals">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Pending Approvals
                </h2>
                <span className="text-sm text-muted-foreground">
                  {pendingApprovals.length} waiting
                </span>
              </div>
              <div className="space-y-3">
                {pendingApprovals.map((req) => (
                  <PendingApprovalCard
                    key={req.id}
                    req={req}
                    viewerId={user?.id}
                    expanded={!!expandedApprovals[req.id]}
                    onToggle={toggleApprovalDetail}
                    highlighted={linkedRequestId === req.id}
                    cardRef={linkedRequestId === req.id ? linkedCardRef : undefined}
                    onApprove={handleApprove}
                    onDecline={(id) => { setDeclineTarget(id); setDeclineNote(''); }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Everything below is the DASHBOARD proper — hidden on /approvals so that
              screen is just the approvals queue, rather than the whole dashboard with
              a list bolted on top. (The wallchart moved to its own page, Team calendar —
              Warm Hive decision 9, 29/10/2026.) */}
          {!isApprovalsPage && (<>

          {/* Approvals waiting — a nudge, not the queue (that lives on /approvals; see the
              note above). Admins and managers only, and only when something is waiting. */}
          {isManager && (pendingApprovals.length + toilWaiting) > 0 && (
            <div className="flex items-center gap-3 sm:gap-4 rounded-2xl border border-hive-orange/30 bg-hive-pending-bg dark:bg-secondary dark:border-border px-4 sm:px-5 py-3" data-testid="approvals-strip">
              <div className="w-9 h-9 rounded-[10px] bg-hive-orange text-hive-navy flex items-center justify-center flex-shrink-0">
                <Inbox className="w-[18px] h-[18px]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-semibold">
                  {pendingApprovals.length + toilWaiting} {(pendingApprovals.length + toilWaiting) === 1 ? 'request is' : 'requests are'} waiting for you
                </p>
                <p className="text-[13px] text-muted-foreground">
                  {toilWaiting > 0 ? 'Leave and time in lieu that needs your decision' : 'Leave that needs your decision'}
                </p>
              </div>
              <Button variant="outline" size="sm" className="bg-card flex-shrink-0" onClick={() => navigate('/approvals')} data-testid="approvals-strip-review">
                Review <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Your pending requests — shared with My Leave (components/YourPendingRequests) */}
          <YourPendingRequests
            requests={myRequests}
            userId={user?.id || user?.user_id}
            onCancel={(id) => handleCancelLeaveFor(id, null)}
            onChanged={refreshDashboard}
          />

          {/* Overview: personal stats, who's off, upcoming leave, team chart */}
          <DashboardOverview user={user} refreshKey={overviewRefreshKey} />

          {/* Your setup now sits inside DashboardOverview, under Who's off (28/10/2026). */}

          </>)}
          {/* end of dashboard-only content */}
        </div>
      </main>

      {/* Decline with note dialog */}
      {declineTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setDeclineTarget(null)}>
          <div className="bg-card rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg mb-2">Decline leave request</h3>
            <p className="text-sm text-muted-foreground mb-3">Add a note for the employee (optional) — for example, suggest alternative dates.</p>
            <textarea
              value={declineNote}
              onChange={(e) => setDeclineNote(e.target.value)}
              rows={3}
              className="input-hive w-full resize-none"
              placeholder="e.g. We can't cover the 10th, but the 13th is fine — please rebook."
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={() => setDeclineTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => handleDecline(declineTarget, declineNote)}>Decline request</Button>
            </div>
          </div>
        </div>
      )}

      {/* The cancel box — the whole booking or just some of its days (DESIGN-part-cancel.md). */}
      <CancelLeaveDialog requestId={cancelId} onClose={() => setCancelId(null)} onDone={refreshDashboard} />

      {/* Request Leave Dialog */}
      <RequestLeaveDialog 
        open={requestDialogOpen} 
        onOpenChange={(open) => {
          setRequestDialogOpen(open);
          if (!open) {
            setQuickBookDate(null);
            setQuickBookLeaveTypeId(null);
            setBookForUser(null);
          }
        }}
        onSuccess={refreshDashboard}
        initialDate={quickBookDate}
        initialLeaveTypeId={quickBookLeaveTypeId}
        bookForUserId={bookForUser?.id}
        bookForUserName={bookForUser?.name}
        allowPastDates={!!bookForUser}
      />
      
    </div>
  );
};

export default DashboardPage;
